import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, type Drueckteil } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import BeistelteileSektion from '../components/BeistelteileSektion';
import BeistelteileBanner from '../components/BeistelteileBanner';
import { KommentarVerlauf } from '../components/KommentarVerlauf';
import { VorkasseBanner } from '../components/VorkasseBanner';

export function Detail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const numId = parseInt(id ?? '0');

  const detail     = useApi(() => api.drueckteile.get(numId), [numId]);
  const kunden     = useApi(() => api.lookup.kunden(), []);
  const users      = useApi(() => api.lookup.users(), []);
  const standWerte = useApi(() => api.lookup.standWerte(), []);
  const history    = useApi(() => api.drueckteile.history(numId), [numId]);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Drueckteil>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (detail.data) setForm(detail.data.data);
  }, [detail.data]);

  if (detail.loading) return <div className="p-8 text-slate-500">Lädt …</div>;
  if (detail.error)   return <div className="p-8 text-red-600">Fehler: {detail.error.message}</div>;
  if (!detail.data)   return null;

  const d = detail.data.data;
  const upd = <K extends keyof Drueckteil>(k: K, v: Drueckteil[K] | null) =>
    setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.drueckteile.update(d.id, {
        ...form,
        updated_at: d.updated_at, // optimistic concurrency
      } as Partial<Drueckteil>);
      setEditing(false);
      detail.reload();
      history.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const deleteIt = async () => {
    if (!confirm(`Auftrag "${d.bestellnr}" wirklich löschen? (Soft-Delete, nachvollziehbar)`)) return;
    try {
      await api.drueckteile.delete(d.id);
      nav('/drueckteile');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
    }
  };

  const toggleFertig = async () => {
    const neu = d.fertig === 1 ? 0 : 1;
    const msg = neu === 0
      ? `Auftrag "${d.bestellnr}" wieder ÖFFNEN?\nFertigungsdatum/Werker werden zurückgesetzt, Stand → Offen.`
      : `Auftrag "${d.bestellnr}" als FERTIG markieren?`;
    if (!confirm(msg)) return;
    setSaving(true); setError(null);
    try {
      await api.drueckteile.update(d.id, {
        fertig: (neu as unknown) as Drueckteil['fertig'],
        updated_at: d.updated_at,
      } as Partial<Drueckteil>);
      detail.reload();
      history.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status-Änderung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      <Link to="/drueckteile" className="text-sm text-brand hover:underline">← Zurück zur Übersicht</Link>

      <header className="mt-4 mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase text-slate-500">Drückteil</div>
          <h1 className="text-2xl font-bold">{d.kunde_name} · {d.masse_bezeichnung ?? '—'}</h1>
          <div className="font-mono text-sm text-slate-500 mt-1">
            Bestellnr: {d.bestellnr} · DV: {d.dv_nummer ?? '—'} · Pos: {d.position ?? '—'}
          </div>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <>
              <button onClick={() => setEditing(true)} className="btn-primary">Bearbeiten</button>
              {d.fertig === 1 ? (
                <button
                  onClick={toggleFertig}
                  disabled={saving}
                  className="btn-secondary text-amber-700 border-amber-200 hover:bg-amber-50"
                  title="Auftrag wieder in Produktion nehmen"
                >
                  ↶ Wieder öffnen
                </button>
              ) : (
                <button
                  onClick={toggleFertig}
                  disabled={saving}
                  className="btn-secondary text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  title="Als fertig markieren"
                >
                  ✓ Fertig
                </button>
              )}
              <button onClick={deleteIt} className="btn-secondary text-red-700 border-red-200 hover:bg-red-50">
                Löschen
              </button>
            </>
          )}
          {editing && (
            <>
              <button onClick={() => { setEditing(false); setForm(d); setError(null); }}
                      className="btn-secondary">Abbrechen</button>
              <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
                {saving ? 'Speichert …' : '✓ Speichern'}
              </button>
            </>
          )}
        </div>
      </header>

      <BeistelteileBanner auftragModul="drueckteile" auftragId={d.id} />

      {!editing && (
        <VorkasseBanner modul="drueckteile" recordId={d.id} vorkasse={d.vorkasse} bezahltAm={d.vorkasse_bezahlt_am}
                         onChanged={() => detail.reload()} />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {d.stand_wert && !editing && (
        <div className="mb-6"><StatusBadge label={d.stand_wert} color={d.stand_farbe} /></div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <EditField label="Kunde" editing={editing}
          display={d.kunde_name ?? '—'}
          edit={
            <select value={form.kunde_id ?? ''} onChange={e => upd('kunde_id', e.target.value ? Number(e.target.value) : null)} className="input">
              <option value="">—</option>
              {kunden.data?.data.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          } />

        <EditField label="Sachbearbeiter" editing={editing}
          display={d.sachbearbeiter_name ?? '—'}
          edit={
            <select value={form.sachbearbeiter_id ?? ''} onChange={e => upd('sachbearbeiter_id', e.target.value ? Number(e.target.value) : null)} className="input">
              <option value="">—</option>
              {users.data?.data.filter(u => u.kurzel !== 'SYS').map(u =>
                <option key={u.id} value={u.id}>{u.name}</option>
              )}
            </select>
          } />

        <EditField label="Maße / Bezeichnung" editing={editing}
          display={d.masse_bezeichnung ?? '—'}
          edit={
            <input value={form.masse_bezeichnung ?? ''} onChange={e => upd('masse_bezeichnung', e.target.value)} className="input" />
          } />

        <EditField label="Material" editing={editing}
          display={d.material ?? '—'}
          edit={<input value={form.material ?? ''} onChange={e => upd('material', e.target.value)} className="input" />} />

        <EditField label="Stück" editing={editing}
          display={String(d.stueck)}
          edit={<input type="number" min={1} value={form.stueck ?? 1} onChange={e => upd('stueck', parseInt(e.target.value) || 1)} className="input" />} />

        <EditField label="Zeit pro Stück (h)" editing={editing}
          display={d.zeit_pro_stueck ? `${d.zeit_pro_stueck} h` : '—'}
          edit={<input type="number" step="0.25" value={form.zeit_pro_stueck ?? ''} onChange={e => upd('zeit_pro_stueck', e.target.value ? Number(e.target.value) : null)} className="input" />} />

        <EditField label="Gesamtzeit (auto)" editing={false}
          display={`${d.gesamtzeit} h`} />

        <EditField label="Priorität" editing={editing}
          display={d.priority}
          edit={
            <select value={form.priority ?? 'normal'} onChange={e => upd('priority', e.target.value as Drueckteil['priority'])} className="input">
              <option value="normal">Normal</option>
              <option value="eilig">Eilig</option>
              <option value="kritisch">Kritisch</option>
            </select>
          } />

        <EditField label="Liefertermin" editing={editing}
          display={formatDate(d.lieferdatum)}
          edit={<input type="date" value={form.lieferdatum ?? ''} onChange={e => upd('lieferdatum', e.target.value)} className="input" />} />

        <EditField label="Intern (-3 Tage, auto)" editing={false}
          display={formatDate(d.lieferdatum_minus3)} />

        <EditField label="KW / Jahr (auto)" editing={false}
          display={`KW ${d.kw} / ${d.jahr}`} />

        <EditField label="Stand" editing={editing}
          display={d.stand_wert ? <StatusBadge label={d.stand_wert} color={d.stand_farbe} /> : '—'}
          edit={
            <select value={form.stand_id ?? ''} onChange={e => upd('stand_id', e.target.value ? Number(e.target.value) : null)} className="input">
              <option value="">— keiner —</option>
              {standWerte.data?.data.map(s => <option key={s.id} value={s.id}>{s.wert}</option>)}
            </select>
          } />

        <EditField label="Fertig?" editing={editing}
          display={d.fertig ? '✓ Ja' : 'Nein'}
          edit={
            <label className="inline-flex items-center gap-2 mt-2">
              <input type="checkbox" checked={Boolean(form.fertig)}
                     onChange={e => upd('fertig', (e.target.checked ? 1 : 0) as unknown as Drueckteil['fertig'])} />
              <span>Als fertig markieren</span>
            </label>
          } />
      </div>

      <div className="mt-6">
        <label className="text-xs text-slate-600 font-medium block mb-1">Kommentar</label>
        {editing ? (
          <textarea value={form.kommentar ?? ''} onChange={e => upd('kommentar', e.target.value)}
                    className="input" rows={3} />
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm min-h-[4rem]">
            {d.kommentar ?? <span className="text-slate-400">—</span>}
          </div>
        )}
      </div>

      {!editing && (
        <BeistelteileSektion auftragModul="drueckteile" auftragId={d.id} />
      )}

      {!editing && <KommentarVerlauf modul="drueckteile" recordId={d.id} />}

      {!editing && history.data && history.data.data.length > 0 && (
        <div className="mt-10">
          <h2 className="font-semibold text-slate-900 mb-3">Verlauf / Audit-Trail</h2>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Zeit</th>
                  <th className="text-left px-4 py-2">Benutzer</th>
                  <th className="text-left px-4 py-2">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.data.data.map((h, i) => {
                  const row = h as { changed_at: string; user_name?: string; action: string };
                  return (
                    <tr key={i}>
                      <td className="px-4 py-2 text-slate-600">{new Date(row.changed_at).toLocaleString('de-DE')}</td>
                      <td className="px-4 py-2">{row.user_name ?? '—'}</td>
                      <td className="px-4 py-2">{row.action}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function EditField({ label, display, edit, editing }: {
  label: string; display: React.ReactNode; edit?: React.ReactNode; editing: boolean;
}) {
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200">
      <div className="text-xs uppercase text-slate-500 mb-1">{label}</div>
      {editing && edit ? edit : <div className="text-sm font-medium">{display}</div>}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
